import { Component, HostListener } from '@angular/core';
import {
  merge,
  Observable,
  share,
  Subject,
  switchMap,
  map,
  tap,
} from 'rxjs';
import { KkmsService } from '../../../core/services/kkms.service';
import { KkmsPageState } from './kkms-page.state';
import { RetailPlacesStateSorting, RetailPlaceUpdaters } from './kkms-page.types';
import { PaginationService } from '../../../core/features/pagination-service/pagination.service';
import { KKMs_PAGE_SIZE, RETAIL_PLACES_SORTING } from './kkms-page.config';
import { KkmsApiService } from '../../../core/modules/kkms/kkms-api.service';
import { FilterKkmsRequest } from '../../../core/modules/kkms/kkms.types';
import { IRetailPlace } from '../../../core/modules/retail-places/retail-paces.types';

@Component({
  selector: 'tax-kkms-page',
  templateUrl: './kkms-page.component.html',
  styleUrls: ['./kkms-page.component.sass'],
})

export class KkmsPageComponent {
  events: RetailPlaceUpdaters = {
    applyFilter$: new Subject<void>(),
    paginationChange$: new Subject<void>(),
    reloadState$: new Subject<void>(),
    searchChange$: new Subject<void>(),
    sort$: new Subject<FilterKkmsRequest['sortField']>(),
  };

  searchField?: string = this.kkmsPageState.getSearchString();
  sorting: RetailPlacesStateSorting = this.kkmsPageState.getSorting();

  readonly sortArray = RETAIL_PLACES_SORTING;
  readonly applySearchByString$ = new Subject<void>();
  readonly pagination$ = this.paginationService.getPaginationState(KKMs_PAGE_SIZE);
  readonly retailPlaces$ = this.getRetailPlaces();

  private isNonUserScrollEnded = false;
  private retailPlaces: IRetailPlace[] = [];

  constructor(
    private kkmsApiService: KkmsApiService,
    private kkmsService: KkmsService,
    private paginationService: PaginationService,
    private kkmsPageState: KkmsPageState,
  ) {}

  @HostListener('window:scroll')
  onScroll(): void {
    /** Проблемы
     * 1. Если мы находимся не на этой странице и позиция скрола !== 0, и
     * возвращаемся на эту страницу, то происходит браузерное событие scroll.
     * В результате сохранённая позиция перетирается.
     * 2. Если при использовании метода Location.back() мы возвращаемся
     * на эту страницу, то происходит событие scroll.
     * В результате сохранённая позиция перетирается.
     *
     * Решение
     * Надо вызывать сохранение позиции скрола после того, как выполнится
     * событие скрола из случаев выше.
     * */
    if (this.isNonUserScrollEnded) {
      this.kkmsPageState.setUserScreenPosition(window.scrollY);
    }
  }

  scrollToPosition(): void {
    this.isNonUserScrollEnded = true;
    window.scroll(0, this.kkmsPageState.getUserScreenPosition());
  }

  resetSearch(): void {
    this.searchField = undefined;
    this.events.searchChange$.next();
  }

  private setSorting(sortField: FilterKkmsRequest['sortField']): void {
    if (this.sorting.sortField === sortField) {
      switch (this.sorting.sortingDirection) {
        case undefined:
          this.sorting.sortingDirection = 'asc';
          break;
        case 'asc':
          this.sorting.sortingDirection = 'desc';
          break;
        case 'desc':
          this.sorting.sortField = undefined;
          this.sorting.sortingDirection = undefined;
          break;
      }
    } else {
      this.sorting = {
        sortField,
        sortingDirection: 'asc',
      };
    }
    this.kkmsPageState.setSorting(this.sorting);
  }

  private applyFilter(): void {
    this.resetKkmsPageState();
    window.scroll(0, 0);
  }

  private updatePaginationState(): void {
    this.paginationService.setPaginationState({
      currentPage: this.paginationService.getPagination().currentPage + 1,
      initialPage: this.paginationService.getPagination().currentPage + 1,
    });
  }

  private reloadState(): void {
    this.retailPlaces = [];
    this.paginationService.setPaginationState({
      initialPage: 0,
    });
  }

  private updateSearchChange(): void {
    this.resetKkmsPageState();
    this.kkmsPageState.setSearchString(this.searchField);
    this.applySearchByString$.next();
  }

  private sortRetailPlaces(sortField: FilterKkmsRequest['sortField']): void {
    this.resetKkmsPageState();
    this.setSorting(sortField);
  }

  private updateRetailPlaces(retailPlaces: IRetailPlace[]): IRetailPlace[] {
    return [
      ...this.retailPlaces,
      ...retailPlaces.map(retailPlace => ({
        ...retailPlace,
        kkms: this.kkmsService.sortKkms(retailPlace.kkms),
      })),
    ];
  }

  private resetKkmsPageState(): void {
    this.paginationService.resetPaginationState(KKMs_PAGE_SIZE);
    this.retailPlaces = [];
  }

  private getRetailPlaces(): Observable<IRetailPlace[]> {
    return merge(
      this.events.applyFilter$.asObservable().pipe(tap(this.applyFilter.bind(this))),
      this.events.paginationChange$.asObservable().pipe(tap(this.updatePaginationState.bind(this))),
      this.events.reloadState$.asObservable().pipe(tap(this.reloadState.bind(this))),
      this.events.searchChange$.asObservable().pipe(tap(this.updateSearchChange.bind(this))),
      this.events.sort$.asObservable().pipe(tap(this.sortRetailPlaces.bind(this))),
    )
      .pipe(
        switchMap(() => this.kkmsApiService.fetchRetailPlacesWithKkms(this.kkmsPageState.getFullState())),
        tap((retailPlaces) => {
          this.paginationService.setPaginationState({
            isLastPage: retailPlaces.length < this.paginationService.getPagination().pageSize,
          });
        }),
        map(this.updateRetailPlaces.bind(this)),
        tap((retailPlaces) => { this.retailPlaces = retailPlaces; }),
        share(),
      );
  }
}
